/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './advertiser-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {AdvertiserInviteEditorModalComponent} from './advertiser-invite-editor-modal.component';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {IAB_CATEGORIES_V1} from '../../components/util/iab-categories-v1';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {MoneyHelper} from '../../components/util/money';
import {
  convertToApiBudgets,
  convertToApiEntities,
  convertToUiBudgets,
  convertToUiEntities,
  doTheCopyOfEntity
} from '../base/nude/advertiser-budgets';
import {buildElectionFormOptions, buildElectionSelectGroups, getElectionLabel} from '../../components/util/election-form-options';
import {linkedPoliticalCampaignRefs} from '../../components/util/political-campaign-advertiser-topics';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {EmailTranslationResource} from '../../components/api/resources/resource/email-translation.resource';
import {PublicPoliticalCampaignResource} from '../../components/api/resources/resource/political-campaign-public.resource';
import {UserResource} from '../../components/api/resources/resource/user.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {BrowserLocationService} from '../angular-platform-providers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {compareById} from '../common/util/object-id-comparison';

function resolveElection(election: any): any {
  if (!election) {
    return null;
  }
  const networkElections = LocalNetworkInfo.obtain().getSuppInfo('elections') || [];
  if (_.isString(election)) {
    return _.find(networkElections, (candidate: any) => {
      return candidate.id === election || candidate.name === election;
    }) || {id: election, name: election};
  }
  const networkElection = _.find(networkElections, (candidate: any) => {
    return (_.get(election, 'id') && candidate.id === election.id)
      || (_.get(election, 'name') && candidate.name === election.name);
  });
  return _.assign({}, networkElection || {}, election);
}

function formatElectionLabel(election: any): string {
  const resolvedElection = resolveElection(election);
  return resolvedElection ? getElectionLabel(resolvedElection) : '';
}

function politicalCampaignDetail(ref: any, campaign: any): any {
  return {
    publicId: ref.publicId,
    id: ref.id || campaign.id,
    name: ref.name || campaign.name,
    campaign: campaign,
    electionLabel: formatElectionLabel(campaign.election),
    expanded: false
  };
}

@Component({
  standalone: false,
  selector: 'adn-advertiser-edit-page',
  template
})
export class AdvertiserEditPageComponent extends AdminResourceEditPageBase {
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  readonly entityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];

  suiPlatform = false;
  isMarketplace = false;
  advertiserInfo = false;
  canSeeTechFees = false;
  isSpecialUser = false;
  showTeams = false;
  hasAdvertiserEnforced = false;
  networkCurrencies: string[] = [];
  availableCurrencies: string[] = [];
  teams: any[] = [];
  viewOnlyTeams: any[] = [];
  users: any[] = [];
  userSearch = false;
  iabTaxonomy: any;
  iabCategoriesV1: any;
  entities: any[] = [];
  elections: any[] = [];
  electionSelectGroups: any = {dated: [], undated: [], useGroups: false, groups: []};
  politicalCampaignDetails: any[] = [];
  politicalCampaignDetailsResolved = false;
  hasLinkedPoliticalCampaigns = false;
  isAxAdvertiserAdmin = false;
  canSetPolitical = false;
  canSeeEditorInvite = false;
  blockSubmit = false;
  teamsWarning: any;

  private defaultAdvertiserInviteSubject = 'You are invited to update Advertiser details';
  private username: string;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly angularModalService: AngularModalService,
    @Inject(PublicPoliticalCampaignResource) private readonly PublicPoliticalCampaign: any,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(EmailTranslationResource) private readonly EmailTranslation: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(UserResource) private readonly User: any,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.suiPlatform = LocalNetworkProfile.isSuiPlatform();
    this.isMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.canSeeTechFees = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalNetworkProfile.isPureMarketplacePlatform()
      || LocalNetworkProfile.hasAdvertiserEnforced();

    this.username = localUserProfile.get('username');
    const isLocalhost = this.location.hostname === 'localhost';
    this.isSpecialUser = (isLocalhost && this.username.startsWith('admin@'))
      || this.username === 'adnuntius@adnuntius.com'
      || this.username === 'broker1@adnuntius.com';

    this.showTeams = LocalNetworkInfo.obtain().getInfo('teamsCount') > 1 || !LocalNetworkProfile.hasAdvertiserEnforced();
    this.hasAdvertiserEnforced = LocalNetworkProfile.hasAdvertiserEnforced();

    const defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });

    this.availableCurrencies = ['DKK', 'EUR', 'NOK', 'SEK'];
    if (this.location.hostname === 'localhost') {
      this.availableCurrencies.push('AUD');
    }

    const re = /\{\{else\}\}([\s\S]*?)\{\{\/if\}\}/;
    this.EmailTranslation.query({
      showEditableOnly: true,
      filterByParam: 'messageDefinition',
      filterByValue: 'ADVERTISER_UPDATE_INVITE'
    }, (data: any) => {
      _.forEach(data.results, (result: any) => {
        if (result.validationWarnings.length === 0) {
          this.defaultAdvertiserInviteSubject = _.get(result.emailSubject.match(re), [1], '') || this.defaultAdvertiserInviteSubject;
        }
      });
    });

    this.iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);
    this.iabCategoriesV1 = _.cloneDeep(IAB_CATEGORIES_V1);

    this.Team.query({pageSize: 10001}, (page: any) => {
      // [] → results flips *ngFor on the owner-team <select> mid-CD (NG0100).
      deferMarkForCheck(this.cdr, () => {
        const allTeams = page.results;
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        const firstFilterTeams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams = _.filter(firstFilterTeams, (t: any) => {
          return t.type === 'AX_ADVERTISER' || t.type === 'STANDARD' || t.type === 'SUI';
        });
        this.viewOnlyTeams = _.filter(firstFilterTeams, (t: any) => {
          return t.type === 'AX_ADVERTISER' || t.type === 'STANDARD' || t.type === 'CREATIVE_AGENCY';
        });
        this.resolveOwnerTeamReference();
        if ((this.isAxAdvertiserAdmin || this.hasAdvertiserEnforced) && allTeams.length > 0 && this.teams.length > 0 && !this.editingModel.ownerTeam) {
          this.editingModel.ownerTeam = this.editingModel.ownerTeam || this.teams[0];
        }
        this.resumeFormDirtyTracking();
      });
    });
  }

  editsMade(): void {
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  readonly compareById = compareById;

  trackByIndex(index: number): number {
    return index;
  }

  teamChanged(): void {
    this.updateUsers();
    this.onFieldChange();
  }

  addElectionBudget(): void {
    this.editingModel.uiPoliticalBudgets = this.editingModel.uiPoliticalBudgets || [];
    this.editingModel.uiPoliticalBudgets.push({});
    this.editsMade();
  }

  removeElection(index: number): void {
    _.pullAt(this.editingModel.uiPoliticalBudgets, index);
    this.editsMade();
  }

  removeCampaign(budget: any, index: number): void {
    _.pullAt(budget.campaigns, index);
    this.editsMade();
  }

  addCampaign(budget: any): void {
    budget.campaigns = budget.campaigns || [];
    budget.campaigns.push({campaignName: '', budget: {}});
    this.editsMade();
  }

  inviteEditor = (): void => {
    this.angularModalService.show(AdvertiserInviteEditorModalComponent, {
      class: 'modal-md',
      initialState: {
        advertiserId: this.editingModel.id,
        defaultSubject: this.defaultAdvertiserInviteSubject
      }
    });
  };

  removeLayoutFee = (index: number): void => {
    _.pullAt(this.editingModel.uiLayoutFees, index);
    if (this.editingModel.uiLayoutFees.length === 0) {
      this.addLayoutFee();
    }
    this.editsMade();
  };

  copyToEntity = (entity: any): void => {
    this.editsMade();
    doTheCopyOfEntity(this.editingModel, entity);
  };

  addLayoutFee = (): void => {
    this.editingModel.uiLayoutFees = this.editingModel.uiLayoutFees || [];
    const defaultFee = {fee: {type: 'MULTI_CURRENCY_MONETARY'}};
    this.initFeesForParam(defaultFee.fee);
    this.editingModel.uiLayoutFees.push(defaultFee);
    this.editsMade();
  };

  togglePoliticalCampaignDetail(detail: any): void {
    detail.expanded = !detail.expanded;
  }

  get showReadonlyLayoutFees(): boolean {
    return this.hasAdvertiserEnforced && !!_.get(this.editingModel, ['uiLayoutFees', 0, 'layout', 'name']);
  }

  protected afterInitEditingModel(): void {
    this.editingModel.address = this.editingModel.address || {country: ''};
    this.editingModel.type = this.editingModel.type || null;
    this.advertiserInfo = !!this.isNew;
    this.blockSubmit = !!this.readOnly;
    this.resolveOwnerTeamReference();
    this.updateUsers();
    this.refreshPoliticalState();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this.editingModel.teams = _.map(_.get(saveObj, 'teams') || this.editingModel.teams, 'id');
    this.editingModel.viewOnlyTeams = _.map(_.get(saveObj, 'viewOnlyTeams') || this.editingModel.viewOnlyTeams, 'id');
    this.resolveOwnerTeamReference();
    this.initialiseLayoutFees();
    this.refreshPoliticalState(saveObj);
    this.updateUsers();
  }

  protected onBeforeSubmit(): void {
    this.editingModel.layoutFees = _.filter(this.editingModel.uiLayoutFees, (layoutFee: any) => {
      return _.get(layoutFee, ['layout', 'id']) && _.filter(_.map(_.get(layoutFee, ['fee', 'multiCurrencyMonetaryAmounts']), (m: any) => {
        return MoneyHelper.fixMoney(m);
      }), (f: any) => {
        return MoneyHelper.isMoney(f);
      }).length > 0;
    });
    this.editingModel.layoutFees = _.map(this.editingModel.layoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee);
      adjFee.fee.multiCurrencyMonetaryAmounts = _.filter(_.map(adjFee.fee.multiCurrencyMonetaryAmounts, (m: any) => {
        return MoneyHelper.fixMoney(m);
      }), (f: any) => {
        return MoneyHelper.isMoney(f);
      });
      return adjFee;
    });
    this.editingModel.politicalBudgets = convertToApiBudgets(this.editingModel.uiPoliticalBudgets);
    convertToApiEntities(this.editingModel);

    if (!this.editingModel.enablePolitical) {
      this.editingModel.type = null;
    }
  }

  private refreshPoliticalState(saveObj?: any): void {
    const ownerTeam = this.editingModel.ownerTeam;
    const ownerTeamId = _.isString(ownerTeam) ? ownerTeam : _.get(ownerTeam, 'id');

    this.isAxAdvertiserAdmin = this.LocalUserPermissions.hasNetwork('AX_ADVERTISER_ADMIN')
      || !!(ownerTeamId && this.LocalUserPermissions.has('AX_ADVERTISER_TEAM_ADMIN', ownerTeamId));

    this.canSetPolitical = this.LocalUserPermissions.hasNetwork('MANAGE_BUSINESS') || this.isAxAdvertiserAdmin;
    this.canSeeEditorInvite = !!(this.editingModel.ownerTeam && this.canSetPolitical);

    const workingObj = saveObj || this.editingModel;
    this.editingModel.uiPoliticalBudgets = convertToUiBudgets(workingObj);
    convertToUiEntities(workingObj);
    this.entities = [this.editingModel.controllingEntity, this.editingModel.payingEntity];
    this.editingModel.enablePolitical = !!this.editingModel.type;
    this.elections = buildElectionFormOptions(LocalNetworkInfo.obtain().getSuppInfo('elections') || [], {
      username: this.username,
      canSeeTestElections: this.localUserProfile.canSeeTestElections(),
      selectedElectionIds: _.map(this.editingModel.uiPoliticalBudgets || [], (budget: any) => {
        return _.get(budget, ['election', 'id']);
      }),
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);
    this.loadPoliticalCampaignDetails();
  }

  private initFeesForParam(ref: any): void {
    _.forEach(this.networkCurrencies, (currency: string) => {
      const foundFee = _.find(_.get(ref, 'multiCurrencyMonetaryAmounts', []), (fee: any) => {
        return fee.currency === currency;
      });
      if (!foundFee) {
        const monetary = _.get(ref, 'monetary');
        if (monetary && monetary.currency === currency) {
          ref.type = 'MULTI_CURRENCY_MONETARY';
          ref.multiCurrencyMonetaryAmounts = ref.multiCurrencyMonetaryAmounts || [];
          ref.multiCurrencyMonetaryAmounts.push(monetary);
        } else {
          ref.multiCurrencyMonetaryAmounts = ref.multiCurrencyMonetaryAmounts || [];
          ref.multiCurrencyMonetaryAmounts.push({currency: currency});
        }
      }
    });
    ref.multiCurrencyMonetaryAmounts = _.orderBy(ref.multiCurrencyMonetaryAmounts, (fee: any) => {
      return this.networkCurrencies.indexOf(fee.currency);
    });
  }

  private initialiseLayoutFees(): void {
    const layoutFees = _.get(this.editingModel, 'layoutFees');
    this.editingModel.uiLayoutFees = _.isArray(layoutFees) ? layoutFees : [];
    this.editingModel.uiLayoutFees[0] = this.editingModel.uiLayoutFees[0] ? this.editingModel.uiLayoutFees[0] : {};
    this.editingModel.uiLayoutFees = _.map(this.editingModel.uiLayoutFees, (layoutFee: any) => {
      const adjFee = _.cloneDeep(layoutFee) || {};
      adjFee.fee = adjFee.fee || {type: 'MULTI_CURRENCY_MONETARY'};
      this.initFeesForParam(adjFee.fee);
      return adjFee;
    });
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.teams.length) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.teams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }

  private updateUsers(): void {
    this.userSearch = false;
    if (this.suiPlatform && _.get(this.editingModel, ['ownerTeam', 'id'])) {
      this.userSearch = true;
      this.User.query({selfServiceOnly: true, team: this.editingModel.ownerTeam.id}).$promise.then((page: any) => {
        deferMarkForCheck(this.cdr, () => {
          this.users = page.results;
          this.userSearch = false;
        });
      }, () => {
        deferMarkForCheck(this.cdr, () => {
          this.userSearch = false;
        });
      });
    }
  }

  private loadPoliticalCampaignDetails(): void {
    const linkedCampaigns = linkedPoliticalCampaignRefs(this.editingModel);
    this.hasLinkedPoliticalCampaigns = linkedCampaigns.length > 0;
    if (!this.hasLinkedPoliticalCampaigns) {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.politicalCampaignDetails = [];
        this.politicalCampaignDetailsResolved = true;
      });
      return;
    }
    this.politicalCampaignDetailsResolved = false;
    Promise.all(_.map(linkedCampaigns, (ref: any) => {
      return this.PublicPoliticalCampaign.get({id: ref.publicId}).$promise.then((campaign: any) => {
        return politicalCampaignDetail(ref, campaign);
      }, () => {
        return {
          publicId: ref.publicId,
          id: ref.id,
          name: ref.name,
          loadError: true,
          expanded: false
        };
      });
    })).then((results: any[]) => {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.politicalCampaignDetails = results;
        this.politicalCampaignDetailsResolved = true;
      });
    });
  }
}
